import {
  Component,
  Optional,
  OnChanges,
  Input,
  input,
  computed,
  AfterContentInit,
  runInInjectionContext,
  inject,
  Injector,
  effect
} from '@angular/core';
import { proj, Coordinate } from 'openlayers';
import { MapComponent } from './map.component';
import { GeometryPointComponent, GeometryLinestringComponent, GeometryPolygonComponent } from './geometry.components';
import { ViewComponent } from './view.component';
import { OverlayComponent } from './overlay.component';

@Component({
  selector: 'aol-coordinate',
  template: `<div class="aol-coordinate"></div>`
})
export class CoordinateComponent implements AfterContentInit {
  private readonly injector = inject(Injector);

  readonly x = input.required<number>();
  readonly y = input.required<number>();
  readonly srid = input<string>('EPSG:3857');

  readonly map = inject(MapComponent);
  readonly viewHost = inject(ViewComponent, { optional: true });
  readonly geometryPointHost = inject(GeometryPointComponent, { optional: true });
  readonly overlayHost = inject(OverlayComponent, { optional: true });
  readonly host = this.viewHost || this.geometryPointHost || this.overlayHost;

  transformedCoordinates = computed<Coordinate>(() => {
    let referenceProjection: proj.Projection;
    let referenceProjectionCode: string;

    referenceProjection = this.map.instance.getView().getProjection();
    referenceProjectionCode = referenceProjection ? referenceProjection.getCode() : 'EPSG:3857';

    if (this.srid() === referenceProjectionCode) {
      return [this.x(), this.y()];
    } else {
      return proj.transform([this.x(), this.y()], this.srid(), referenceProjectionCode);
    }
  });

  ngAfterContentInit() {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        if (this.host instanceof GeometryPointComponent) {
          this.host.instance.setCoordinates(this.transformedCoordinates());
          return;
        }

        if (this.host instanceof ViewComponent) {
          this.host.instance.setCenter(this.transformedCoordinates());
          return;
        }

        if (this.host instanceof OverlayComponent) {
          this.host.instance.setPosition(this.transformedCoordinates());
          return;
        }
      });
    });
  }
}

@Component({
  selector: 'aol-collection-coordinates',
  template: `<div class="aol-collection-coordinates"></div>`
})
export class CollectionCoordinatesComponent implements OnChanges {
  private host: any;

  @Input() coordinates: [number, number][];
  @Input() srid: string = 'EPSG:3857';

  constructor(
    private map: MapComponent,
    @Optional() geometryLinestring: GeometryLinestringComponent,
    @Optional() geometryPolygon: GeometryPolygonComponent
  ) {
    if (!!geometryLinestring) {
      this.host = geometryLinestring;
    } else if (!!geometryPolygon) {
      this.host = geometryPolygon;
    } else {
      throw new Error('aol-collection-coordinates must be a child of a geometry component');
    }
  }

  ngOnChanges() {
    let referenceProjection: proj.Projection;
    let referenceProjectionCode: string;
    let transformedCoordinates: Array<Coordinate>;

    referenceProjection = this.map.instance.getView().getProjection();
    referenceProjectionCode = referenceProjection ? referenceProjection.getCode() : 'EPSG:3857';

    if (this.srid === referenceProjectionCode) {
      transformedCoordinates = this.coordinates;
    } else {
      transformedCoordinates = [];
      this.coordinates.forEach(
        function (coordinate: Coordinate) {
          transformedCoordinates.push(proj.transform(coordinate, this.srid, referenceProjectionCode));
        }.bind(this)
      );
    }
    switch (this.host.componentType) {
      case 'geometry-linestring':
        this.host.instance.setCoordinates(transformedCoordinates);
        break;
      case 'geometry-polygon':
        this.host.instance.setCoordinates([transformedCoordinates]);
        break;
      default:
        throw new Error("aol-collection-coordinates' host is of unknown type: " + this.host.componentType);
      // break;
    }
  }
}
